/**
 * Запись голосовых сообщений (CLIENT_WEB.md §10.4, экраны C-12, C-13).
 *
 * Формат выбирается первым поддерживаемым; `content_type` передаётся базовым
 * типом без параметров (`;codecs=` отбрасывается, CC §8.1). Оба семейства
 * принимаются сервером — BS §12.2 `chat_voice`: AAC/M4A, OGG/Opus, WebM/Opus.
 */

/** §10.4 п. 2: порядок проб. Ключ — `mimeType`, значение — базовый `content_type`. */
const FORMAT_CANDIDATES: readonly (readonly [string, string])[] = [
  ['audio/webm;codecs=opus', 'audio/webm'],
  ['audio/ogg;codecs=opus', 'audio/ogg'],
  ['audio/mp4', 'audio/mp4'],
]

export type RecorderState = 'idle' | 'requesting' | 'recording' | 'stopped' | 'denied' | 'unsupported'

export interface VoiceRecording {
  blob: Blob
  /** Базовый MIME без параметров — уходит в `POST /media/uploads`. */
  contentType: string
  durationMs: number
  /** Локальный URL для прослушивания до отправки (C-12). */
  previewUrl: string
}

export interface RecorderCallbacks {
  onStateChange?: (state: RecorderState) => void
  /** Длительность в миллисекундах — для таймера на экране. */
  onTick?: (elapsedMs: number) => void
  /** Достигнут предел длительности: запись остановлена автоматически. */
  onLimitReached?: () => void
}

/** Выбирает поддерживаемый формат; `null` — запись недоступна в этом браузере. */
export function pickRecordingFormat(): { mimeType: string; contentType: string } | null {
  if (typeof MediaRecorder === 'undefined') return null

  for (const [mimeType, contentType] of FORMAT_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(mimeType)) return { mimeType, contentType }
  }

  // Некоторые сборки не реализуют isTypeSupported — пробуем формат по умолчанию.
  return { mimeType: '', contentType: 'audio/webm' }
}

export class VoiceRecorder {
  private recorder: MediaRecorder | null = null
  private stream: MediaStream | null = null
  private chunks: Blob[] = []
  private startedAt = 0
  private tickTimer: ReturnType<typeof setInterval> | null = null
  private limitTimer: ReturnType<typeof setTimeout> | null = null
  private contentType = 'audio/webm'
  private state: RecorderState = 'idle'

  constructor(private readonly callbacks: RecorderCallbacks = {}) {}

  getState(): RecorderState {
    return this.state
  }

  private setState(state: RecorderState): void {
    if (this.state === state) return
    this.state = state
    this.callbacks.onStateChange?.(state)
  }

  /**
   * Запрашивает микрофон и начинает запись.
   * Разрешение запрашивается В МОМЕНТ действия пользователя (ПС §18);
   * отказ переводит в состояние `denied` — экран показывает C-13.
   */
  async start(maxDurationSec: number): Promise<boolean> {
    const format = pickRecordingFormat()
    if (!format) {
      this.setState('unsupported')
      return false
    }

    this.setState('requesting')

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      })
    } catch {
      this.setState('denied')
      return false
    }

    this.contentType = format.contentType
    this.chunks = []

    try {
      this.recorder = format.mimeType
        ? new MediaRecorder(this.stream, { mimeType: format.mimeType })
        : new MediaRecorder(this.stream)
    } catch {
      this.releaseStream()
      this.setState('unsupported')
      return false
    }

    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data)
    }

    this.startedAt = Date.now()
    this.recorder.start()
    this.setState('recording')

    this.tickTimer = setInterval(() => {
      this.callbacks.onTick?.(Date.now() - this.startedAt)
    }, 200)

    // §10.4 п. 3: таймер останавливает запись автоматически и показывает это.
    this.limitTimer = setTimeout(() => {
      this.callbacks.onLimitReached?.()
      void this.stop()
    }, maxDurationSec * 1000)

    return true
  }

  /** Останавливает запись и отдаёт результат. */
  stop(): Promise<VoiceRecording | null> {
    const recorder = this.recorder
    if (!recorder || recorder.state === 'inactive') {
      this.cleanup()
      return Promise.resolve(null)
    }

    return new Promise((resolve) => {
      recorder.onstop = () => {
        const durationMs = Date.now() - this.startedAt
        const blob = new Blob(this.chunks, { type: this.contentType })

        this.cleanup()
        this.setState('stopped')

        if (blob.size === 0) {
          resolve(null)
          return
        }

        resolve({
          blob,
          contentType: this.contentType,
          durationMs,
          previewUrl: URL.createObjectURL(blob),
        })
      }

      recorder.stop()
    })
  }

  /** Отмена до отправки (C-12): результат выбрасывается, `complete` не вызывается. */
  cancel(): void {
    const recorder = this.recorder
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null
      recorder.stop()
    }

    this.chunks = []
    this.cleanup()
    this.setState('idle')
  }

  private cleanup(): void {
    if (this.tickTimer) clearInterval(this.tickTimer)
    if (this.limitTimer) clearTimeout(this.limitTimer)
    this.tickTimer = null
    this.limitTimer = null

    this.releaseStream()
    this.recorder = null
  }

  /** §10.4 п. 5: дорожки останавливаются — индикатор записи браузера гаснет. */
  private releaseStream(): void {
    for (const track of this.stream?.getTracks() ?? []) track.stop()
    this.stream = null
  }
}
